/**
 * Copyright (c) 2010-2024 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { CollectionViewer } from '@angular/cdk/collections';
import { DataSource } from '@angular/cdk/table';
import { NgZone } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Store } from '@ngxs/store';
import { isEqual } from 'lodash-es';
import { BehaviorSubject, catchError, combineLatest, Observable, of, Subject, Subscription, throwError } from 'rxjs';
import {
    auditTime,
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    shareReplay,
    startWith,
    switchMap,
    take,
    takeUntil,
    tap
} from 'rxjs/operators';
import { PagingOptions } from '../../../../types/PagingOptions';
import { SortOptions } from '../../../../types/SortOptions';
import { MapConfigService } from '../../../map/services/map-config.service';
import { DashGisUtil } from '../../../shared/dash-gis-util';
import { LoggerService } from '../../../shared/services/logger.service';
import { RestService } from '../../../shared/services/rest.service';
import { ReportService } from '../../services/report.service';
import { FilterOptions, ReportAggregateField, ReportDefinition, ReportDocumentField } from '../../state/report.model';
import { ReportState } from '../../state/report.state';

export interface RunReportOptions {
    report: ReportDefinition;
    keys?: any[];
    page?: number;
    pageLimit?: number;
    sortColumn?: string;
    sortDirection?: string;
    filterOptions?: FilterOptions[];
}

export class ReportDataSource extends DataSource<any> {

    id: string;
    reportHtml: SafeHtml;

    isError$: Observable<boolean>;
    count$: Observable<number>;
    loading$: Observable<boolean>;
    disposing$: Observable<void>;
    reportSubType$: Observable<string>;
    reportKeys$: Observable<any[]>;
    dataFormKeys$: Observable<any[]>;
    reportHtml$: Observable<SafeHtml>;
    hasHtml$: Observable<boolean>;
    reportData$: Observable<any[]>;
    reportRawData$: Observable<any[]>;
    reportAllData$: Observable<any[] | null>;
    reportAllRawData$: Observable<any[]>;
    reportTotals$: Observable<ReportAggregateField[]>;
    reportAverages$: Observable<ReportAggregateField[]>;
    reportPdf$: Observable<string>;

    protected isErrorSubject = new BehaviorSubject<boolean>(false);
    protected countSubject: BehaviorSubject<number>;
    protected disposingSubject: Subject<void>;
    protected loadingSubject: BehaviorSubject<boolean>;
    protected reportKeysSubject: BehaviorSubject<any[]>;
    protected dataFormKeysSubject: BehaviorSubject<any[]>;
    protected reportHtmlSubject: BehaviorSubject<SafeHtml>;
    protected hasHtmlSubject: BehaviorSubject<boolean>;
    protected reportDataSubject: BehaviorSubject<any[] | null>;
    protected reportRawDataSubject: BehaviorSubject<any[] | null>;
    protected reportAllDataSubject: BehaviorSubject<any[] | null>;
    protected reportAllRawDataSubject: BehaviorSubject<any[] | null>;
    protected reportTotalsSubject: BehaviorSubject<ReportAggregateField[]>;
    protected reportAveragesSubject: BehaviorSubject<ReportAggregateField[]>;
    protected reportPdfSubject: Subject<string>;
    protected digitizeKeySubject = new BehaviorSubject<any>(null);

    protected inputChangedSubject = new Subject<void>();

    private paging$: Observable<(id: string, reportName: string) => PagingOptions>;
    private sorting$: Observable<(id: string, reportName: string) => SortOptions>;
    private filters$: Observable<(id: string, reportName: string) => FilterOptions[]>;

    private pagingOptions$: Observable<PagingOptions>;
    private sortingOptions$: Observable<SortOptions>;
    private filterOptions$: Observable<FilterOptions[]>;
    private currentReportSubscription: Subscription;

    constructor(
        protected logger: LoggerService,
        protected ngZone: NgZone,
        protected store: Store,
        protected mapConfig: MapConfigService,
        protected rest: RestService,
        protected sanitizer: DomSanitizer,
        protected reportService: ReportService,
        protected forPrint: boolean,
        private definition: ReportDefinition,
        id: string,
        keys: any[]
    ) {
        super();

        this.id = id;

        this.paging$ = this.store.select(ReportState.paging);
        this.sorting$ = this.store.select(ReportState.sorting);
        this.filters$ = this.store.select(ReportState.filters);

        this.isErrorSubject = new BehaviorSubject<boolean>(false);
        this.isError$ = this.isErrorSubject.asObservable();

        this.countSubject = new BehaviorSubject<number>(-1);
        this.count$ = this.countSubject.asObservable();

        this.disposingSubject = new Subject<void>();
        this.disposing$ = this.disposingSubject.asObservable();

        this.loadingSubject = new BehaviorSubject<boolean>(false);
        this.loading$ = this.loadingSubject.asObservable();

        this.reportKeysSubject = new BehaviorSubject<any[]>(keys);
        this.reportKeys$ = this.reportKeysSubject.asObservable().pipe(distinctUntilChanged(isEqual));

        this.dataFormKeysSubject = new BehaviorSubject<any[]>([]);
        this.dataFormKeys$ = this.dataFormKeysSubject.asObservable();

        this.reportHtmlSubject = new BehaviorSubject<SafeHtml>(null);
        this.reportHtml$ = this.reportHtmlSubject.asObservable();

        this.hasHtmlSubject = new BehaviorSubject<boolean>(false);
        this.hasHtml$ = this.hasHtmlSubject.asObservable();

        this.reportDataSubject = new BehaviorSubject<any[] | null>(null);
        this.reportData$ = this.reportDataSubject.asObservable();

        this.reportRawDataSubject = new BehaviorSubject<any[] | null>(null);
        this.reportRawData$ = this.reportRawDataSubject.asObservable();

        this.reportAllDataSubject = new BehaviorSubject<any[] | null>(null);
        this.reportAllData$ = this.reportAllDataSubject.asObservable();

        this.reportAllRawDataSubject = new BehaviorSubject<any[] | null>(null);
        this.reportAllRawData$ = this.reportAllRawDataSubject.asObservable();

        this.reportTotalsSubject = new BehaviorSubject<ReportAggregateField[]>([]);
        this.reportTotals$ = this.reportTotalsSubject.asObservable();

        this.reportAveragesSubject = new BehaviorSubject<ReportAggregateField[]>([]);
        this.reportAverages$ = this.reportAveragesSubject.asObservable();

        this.reportPdfSubject = new Subject<string>();
        this.reportPdf$ = this.reportPdfSubject.asObservable();

        this.pagingOptions$ = this.paging$.pipe(
            map((fn) => fn(this.id, this.definition.name)),
            distinctUntilChanged((a, b) => isEqual(a, b))
        );

        this.sortingOptions$ = this.sorting$.pipe(
            map((fn) => fn(this.id, this.definition.name)),
            distinctUntilChanged((a, b) => isEqual(a, b))
        );

        this.filterOptions$ = this.filters$.pipe(
            map((fn) => fn(this.id, this.definition.name)),
            distinctUntilChanged((a, b) => isEqual(a, b)),
            map((filters: FilterOptions[]) =>
                // do not pass filters without values
                filters.filter((f: FilterOptions) => {
                    switch (f.type) {
                        case 'string':
                        case 'string-exact':
                            return !DashGisUtil.isNullOrEmpty(f.value);
                        case 'number':
                            return (
                                !DashGisUtil.isNullOrEmpty(f.min) ||
                                f.min === 0 ||
                                !DashGisUtil.isNullOrEmpty(f.max) ||
                                f.max === 0
                            );
                        case 'date':
                            return !!(f.min || f.max);
                    }
                })
            )
        );

        this.reportSubType$ = combineLatest([ this.count$, this.isError$ ]).pipe(
            distinctUntilChanged(isEqual),
            map(([ count, isError ]) => {
                let subtype;
                switch (true) {
                    case isError:
                        subtype = 'error';
                        break;
                    case this.definition.subType === 'label':
                        subtype = 'label';
                        break;
                    case this.definition.subType === 'chart':
                        subtype = 'chart';
                        break;
                    case this.definition.subType === 'csv':
                        subtype = 'csv';
                        break;
                    case count > 1 || this.definition.filters.length > 0:
                        subtype = 'tabular';
                        break;
                    case count === 1:
                        subtype = 'detail';
                        break;
                    case count === 0 && this.definition.filters.length === 0:
                        subtype = 'empty';
                        break;
                    case count < 0:
                        subtype = 'pending';
                        break;
                    default:
                        subtype = this.definition.subType;
                        break;
                }
                return subtype;
            }),
            startWith(this.definition.subType)
        );

        this.initialize();
    }

    connect(collectionViewer: CollectionViewer): Observable<any> {
        return this.forPrint ? this.reportAllData$ : this.reportData$;
    }

    disconnect(collectionViewer: CollectionViewer): void {
        // this.forPrint
        //     ? this.reportAllDataSubject.complete()
        //     : this.reportDataSubject.complete();
    }

    initialize() {
        if (!this.forPrint && this.definition.subType !== 'chart' && this.definition.subType !== 'label') {
            this.inputChangedSubject
                .asObservable()
                .pipe(takeUntil(this.disposing$))
                .subscribe({
                    next: () => this.loadReportData()
                });

            combineLatest([ this.reportKeys$, this.pagingOptions$, this.sortingOptions$, this.filterOptions$ ])
                .pipe(
                    distinctUntilChanged(isEqual),
                    // only take the last if fired within x time, this prevents the same report
                    // being fired multiple times when 2 or more of the above params are
                    // changed together (programmatically)
                    auditTime(150),
                    takeUntil(this.disposing$)
                )
                .subscribe({
                    next: () => this.inputChangedSubject.next()
                });
        }
    }

    dispose() {
        this.disposingSubject.next();

        this.hasHtmlSubject.complete();
        this.reportHtmlSubject.complete();
        this.reportAveragesSubject.complete();
        this.reportTotalsSubject.complete();
        this.reportDataSubject.complete();
        this.reportRawDataSubject.complete();
        this.reportAllDataSubject.complete();
        this.countSubject.complete();
        this.reportPdfSubject.complete();
        this.loadingSubject.complete();

        this.disposingSubject.complete();
    }

    loadReportData() {
        // this.resultCountSubject.next(-1);

        const reportResponse$ = combineLatest([ this.pagingOptions$, this.sortingOptions$, this.filterOptions$ ]).pipe(
            debounceTime(0),
            distinctUntilChanged(isEqual),
            take(1),
            switchMap((options) => this.loadReportDataInternal(options[0], options[1], options[2])),
            shareReplay()
        );

        reportResponse$
            .pipe(
                filter((response) => response && 'reportHtml' in response),
                map((response) => this.sanitizer.bypassSecurityTrustHtml(response.reportHtml))
            )
            .subscribe(
                (html) => {
                    this.reportHtml = html;
                    this.hasHtmlSubject.next(true);
                    this.reportHtmlSubject.next(html);
                },
                (err) => this.logger.error('reportHtml:', err)
            );

        reportResponse$
            .pipe(
                filter((response) => response && 'dataFormKeys' in response),
                map((response) => response.dataFormKeys)
            )
            .subscribe(
                (keys) => this.dataFormKeysSubject.next(keys),
                (err) => this.logger.error(err)
            );

        reportResponse$
            .pipe(
                filter((response) => response && 'data' in response),
                tap((response) => this.reportRawDataSubject.next(response.data)),
                map((response) => this.shapeData(response.data, response.count))
            )
            .subscribe(
                (data) => {
                    this.reportDataSubject.next(data);
                },
                (err) => this.logger.error(err)
            );

        reportResponse$
            .pipe(
                filter((response) => response && 'count' in response),
                map((response) => response.count),
                tap((count) => this.countSubject.next(count))
            )
            .subscribe();

        reportResponse$
            .pipe(
                filter((response) => response && 'totals' in response),
                map((response) => response.totals),
                tap((totals) => this.reportTotalsSubject.next(totals))
            )
            .subscribe();

        reportResponse$
            .pipe(
                filter((response) => response && 'averages' in response),
                map((response) => response.averages),
                tap((averages) => this.reportAveragesSubject.next(averages))
            )
            .subscribe();

        reportResponse$
            .pipe(
                filter((response) => response && 'pdf' in response),
                take(1)
            )
            .subscribe(
                (response) => {
                    this.reportPdfSubject.next(response.pdf);
                },
                (err) => this.logger.error('PDF pipe', err)
            );

        reportResponse$
            .pipe(
                filter((response) => response && 'csv' in response),
                take(1)
            )
            .subscribe(
                (response) => {
                    const csvBlob = new Blob([ response.csv ], { type: 'text/csv' });
                    this.reportService.downloadCSV(csvBlob);
                },
                (err) => this.logger.error('csv pipe', err)
            );
    }

    loadAllReportData() {
        const reportResponse$ = combineLatest([ this.pagingOptions$, this.sortingOptions$, this.filterOptions$ ]).pipe(
            debounceTime(0),
            distinctUntilChanged(isEqual),
            take(1),
            map((options) => {
                options[0] = {
                    page: null,
                    limit: null
                };
                options[1] = {
                    sortColumn: options[1]?.column,
                    sortDirection: options[1]?.direction
                };
                return options;
            }),
            switchMap((options) => this.loadReportDataInternal(options[0], options[1], options[2])),
            shareReplay()
        );

        reportResponse$
            .pipe(
                filter((response) => 'reportHtml' in response),
                map((response) => this.sanitizer.bypassSecurityTrustHtml(response.reportHtml))
            )
            .subscribe(
                (html) => {
                    this.reportHtml = html;
                    this.hasHtmlSubject.next(true);
                    this.reportHtmlSubject.next(html);
                },
                (err) => this.logger.error('reportHtml:', err)
            );

        reportResponse$
            .pipe(
                filter((response) => response && 'data' in response),
                map((response) => response.data),
                tap((data) => this.reportAllRawDataSubject.next(data)),
                map((data) => this.shapeData(data))
            )
            .subscribe(
                (data) => this.reportAllDataSubject.next(data),
                (err) => this.logger.error(err)
            );

        reportResponse$
            .pipe(
                filter((response) => response && 'count' in response),
                map((response) => response.count),
                tap((count) => this.countSubject.next(count))
            )
            .subscribe();

        reportResponse$
            .pipe(
                filter((response) => response && 'totals' in response),
                map((response) => response.totals),
                tap((totals) => this.reportTotalsSubject.next(totals))
            )
            .subscribe();

        reportResponse$
            .pipe(
                filter((response) => response && 'averages' in response),
                map((response) => response.averages),
                tap((averages) => this.reportAveragesSubject.next(averages))
            )
            .subscribe();

        reportResponse$
            .pipe(
                filter((response) => response && 'pdf' in response),
                take(1)
            )
            .subscribe(
                (response) => {
                    this.reportPdfSubject.next(response.pdf);
                },
                (err) => this.logger.error('PDF pipe', err)
            );

        reportResponse$
            .pipe(
                filter((response) => response && 'csv' in response),
                take(1)
            )
            .subscribe(
                (response) => {
                    const csvBlob = new Blob([ response.csv ], { type: 'text/csv' });
                    this.reportService.downloadCSV(csvBlob);
                },
                (err) => this.logger.error('csv pipe', err)
            );
    }

    clearAllReportData() {
        this.reportAllDataSubject.next(null);
        this.reportAllRawDataSubject.next(null);
    }

    shapeData(data: any[], count?: number) {
        // Detail reports need data structured as one row per field. In order to
        // reuse the cell templates, we create a structure that will match the 'normal'
        // layout, adding the label taken from the field definition 'display' entry:
        //
        // { label: <fieldDef.display>, <fieldname>: <value>, ... extra fields }
        //
        // extra fields might include the 'text' field for video links, for example.

        if (!Array.isArray(data)) {
            return DashGisUtil.clone(data);
        }

        const row = data[0];

        const totalRecords = count || data.length;

        if (totalRecords !== 1 || this.definition.filters.length > 0) {
            return DashGisUtil.clone(data);
        }

        // using reduce allows us to filter out fields that won't be displayed, and end up added into
        // the related row
        return this.definition.fields.reduce((acc, fieldDef) => {
            if (fieldDef.hidden || (this.forPrint && fieldDef.disablePrint)) {
                return acc;
            }
            const rowObject: any = {};
            if (fieldDef.name in row) {
                rowObject[fieldDef.name] = row[fieldDef.name];
            }
            rowObject.label = fieldDef.display;
            rowObject.field = fieldDef;
            switch (fieldDef.type) {
                case 'string':
                    break;
                case 'number':
                    break;
                case 'date':
                    break;
                case 'document':
                    const docFieldDef = fieldDef as ReportDocumentField;
                    rowObject[docFieldDef.text] = row[docFieldDef.text];
                    rowObject[docFieldDef.documentPath] = row[docFieldDef.documentPath];
                    break;
                case 'link':
                    rowObject[fieldDef.text] = row[fieldDef.text];
                    break;
                case 'report':
                    rowObject[fieldDef.text] = row[fieldDef.text];
                    rowObject[fieldDef['reportName']] = row[fieldDef['reportName']];
                    if ('reportKey' in fieldDef) {
                        rowObject[fieldDef.reportKey] = row[fieldDef.reportKey];
                    }
                    break;
                case 'video':
                    rowObject[fieldDef.text] = row[fieldDef.text];
                    break;
            }
            acc.push(rowObject);
            return acc;
        }, []);
    }

    setDigitizeKey(value) {
        this.digitizeKeySubject.next(value);
    }


    private loadReportDataInternal(pagingOptions?: PagingOptions, sortOptions?: SortOptions, filterOptions?: FilterOptions[]) {
        if (this.currentReportSubscription) {
            this.currentReportSubscription.unsubscribe();
        }
        this.ngZone.run(() => {
            this.loadingSubject.next(true);
        });

        if (!pagingOptions) {
            pagingOptions = {
                page: null,
                limit: null
            };
        }

        if (!sortOptions) {
            sortOptions = {
                column: null,
                direction: null
            };
        }

        const reportSubject = new BehaviorSubject<any>({});
        const reportResponse$ = reportSubject.asObservable();
        this.currentReportSubscription = this.runReport({
            report: this.definition,
            keys: this.keys,
            // paginator page is 0-based, api is 1-based
            page: pagingOptions.page || pagingOptions.page === 0 ? pagingOptions.page + 1 : null,
            pageLimit: pagingOptions.limit || null,
            sortColumn: sortOptions.column || null,
            sortDirection: sortOptions.direction || null,
            filterOptions: filterOptions || null
        })
            .pipe(
                tap((result) => reportSubject.next(result)),
                tap(() => {
                    this.loadingSubject.next(false);
                    // this.ngZone.run(() => {
                    //     this.loadingSubject.next(false);
                    // });
                }),
                take(1)
            )
            .subscribe({
                error: (err) => this.logger.error(err)
            });

        return reportResponse$;
    }

    /**
     * Call the server and get the report data
     * @param opts {RunReportOptions}
     * @returns {*}
     */
    private runReport(opts: RunReportOptions): Observable<any> {
        const reportName = opts.report.name;
        let route;
        let data;

        if ('data' === opts.report.type.toLowerCase()) {
            route = 'report/data/run';
            data = {};
        } else {
            if (!opts.keys) {
                return throwError('No keys for report');
            }
            const layerName = opts.report.layer;
            route = `report/feature/run/${ this.mapConfig.mapName }/${ layerName }/${ opts.report.type }`;
            data = {
                keys: opts.keys
            };
            if (opts.keys.length === 1) {
                this.setDigitizeKey(opts.keys[0]);
            }
        }
        if ('' !== reportName) {
            data.name = reportName;
        }

        if (opts.page !== null && opts.pageLimit !== null) {
            data.paging = {
                limit: opts.pageLimit,
                page: opts.page
            };
        }
        if (opts.sortColumn && opts.sortDirection) {
            data.paging = {
                ...data?.paging,
                ...{
                    sortColumn: opts.sortColumn,
                    sortDirection: opts.sortDirection
                }
            };
        }

        if (null !== opts.filterOptions) {
            data.filters = [];
            for (const filter of opts.filterOptions) {
                const filterData = {
                    type: filter.type,
                    field: filter.field
                };
                switch (filter.type) {
                    case 'string':
                    case 'string-exact':
                        filterData['value'] = filter.value;
                        break;
                    case 'number':
                        if (filter.min) {
                            filterData['min'] = filter.min;
                        }
                        if (filter.max) {
                            filterData['max'] = filter.max;
                        }
                        break;
                    case 'date':
                        if (filter.min) {
                            filterData['min'] = filter.min;
                        }
                        if (filter.max) {
                            filterData['max'] = filter.max;
                        }
                        break;
                }
                data.filters.push(filterData);
            }
        }
        return of(true).pipe(
            // call api as a higher-order observable, so the switchMap cancels in-process request when a new request is made,
            // and to handle errors without causing the entire observable chain to fail
            switchMap(() => this.rest.apiPost(route, data, {}, { useWebWorker: true, skipErrorHandler: true }).pipe(
                catchError((err) => {
                    this.logger.error('Error fetching report data', err);
                    this.isErrorSubject.next(true);
                    return of({
                        data: []
                    });
                })
            ))
        );
    }

    get keys(): Array<any> {
        return this.reportKeysSubject.getValue();
    }

    set keys(value: Array<any>) {
        this.reportKeysSubject.next(value);
    }

    get digitizeKey$() {
        return this.digitizeKeySubject.asObservable();
    }
}
